import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { memo } from 'react';
import { GroupItem } from '@pages/clients/groups-page/group-item/index.ts';
import { NotFound } from '@pages/not-found/index.ts';
import { InfinityList } from '@widgets/shared/InfinityList/index.tsx';
import { usePublication } from '@widgets/shared/Publication/model.ts';
import { CustomField } from '@widgets/shared/Publication/ui.tsx';
import { PublicationToggle } from '@widgets/shared/Publication/ui/ui/toggle-publication/ui.tsx';
import { Person } from '@shared/assets';
import { ArrowShortLeft, Clock } from '@shared/assets/index.ts';
import type { FetchStatuses } from '@shared/types/requests';
import { IconButton, ListBody, PageHead, PageTitle, TextButton } from '@shared/ui/index.ts';
import { Loader } from '@src/library/components/common/index.ts';
import * as st from './styles.module.scss';
import type { PublicationType } from './types.ts';

interface PublicationPT {
  title?: string;
  status?: FetchStatuses;
  publicationData?: PublicationType;
  entityName: string;
  onUpdatePublication: (
    data: Partial<PublicationType>
  ) => Promise<{ data?: PublicationType; error?: FetchBaseQueryError | SerializedError }>;
}

export const Publication = memo(
  ({
    title = 'Публикация',
    publicationData,
    onUpdatePublication,
    status,
    entityName,
  }: PublicationPT) => {
    const {
      isForNotClient,
      isForFutureGroups,
      isForAll,
      isPublished,
      hasMoreGroups,
      groups,
      onForFutureGroupsClick,
      onForAllClick,
      onForNotClientClick,
      isPublishButtonDisabled,
      isShouldShowReadyButton,
      loadGroups,
      groupsLoaderStatus,
      onPageExit,
      publish,
      unpublish,
      onRedirect,
      specialistsGroupData,
      withoutGroupGroupData,
      onPublicationButtonClick,
      doShowReadyButton,
    } = usePublication({ ...publicationData, onUpdatePublication, entityName });
    // console.log("isForAll:",isForAll)
    // console.log("isForSpecialists:",isForSpecialists)
    // console.log("isForNotClient:",isForNotClient)
    // console.log("isForWithoutGroups:",isForWithoutGroups)
    // console.log("isForFutureGroups:",isForFutureGroups)
    if (status?.isError) {
      console.log('Ошибка бека');
      return <NotFound />;
    }
    return (
      <>
        <PageTitle title={title} />
        <PageHead
          Left={
            <button onClick={onPageExit}>
              <IconButton variant="dark">
                <ArrowShortLeft />
              </IconButton>
            </button>
          }
          Right={
            status?.isSuccess && isShouldShowReadyButton ? (
              <TextButton
                disabled={isPublishButtonDisabled}
                onClick={isPublished ? publish : unpublish}
              >
                Готово
              </TextButton>
            ) : undefined
          }
        />

        <ListBody className={st.publicationBody}>
          {status?.isSuccess ? (
            <>
              <h3 className={st.status}>СТАТУС</h3>
              {
                <PublicationToggle
                  title={isPublished ? 'Опубликована' : 'Не опубликована'}
                  isToggleOn={isPublished}
                  onToggleClick={onPublicationButtonClick}
                />
              }
              {isPublished && (
                <>
                  <h3 className={st.all}>
                    <p className={st.all_title}>ДЛЯ КОГО</p>
                    {isForAll ? undefined : (
                      <TextButton onClick={onForAllClick} variant={'dark'}>
                        Выбрать все
                      </TextButton>
                    )}
                  </h3>

                  <InfinityList
                    className={st.groupsList}
                    hasMore={hasMoreGroups}
                    loadMore={loadGroups}
                    loading={groupsLoaderStatus === 'loading'}
                  >
                    {groups.map((group) => (
                      <GroupItem
                        key={group.id}
                        group={group}
                        onRedirect={onRedirect}
                        onSelecting={doShowReadyButton}
                        linkValue={`/clients/groups/${group.id}`}
                      />
                    ))}
                  </InfinityList>

                  {withoutGroupGroupData && (
                    <GroupItem
                      key={withoutGroupGroupData.id}
                      group={withoutGroupGroupData}
                      onSelecting={doShowReadyButton}
                      onRedirect={onRedirect}
                      linkValue={`/clients/groups/${withoutGroupGroupData.id}`}
                    />
                  )}

                  <CustomField
                    title={'Группы, созданные в будущем'}
                    isActive={isForFutureGroups}
                    onClick={onForFutureGroupsClick}
                    className={st.futureGroupsField}
                    icon={<Clock />}
                  />

                  {specialistsGroupData && (
                    <GroupItem
                      key={specialistsGroupData.id}
                      group={specialistsGroupData}
                      onRedirect={onRedirect}
                      className={st.specialistsGroup}
                      onSelecting={doShowReadyButton}
                      linkValue={`/clients/groups/${specialistsGroupData.id}`}
                    />
                  )}

                  <CustomField
                    title={'Пользователи платформы, не являющиеся моими клиентами'}
                    isActive={isForNotClient}
                    onClick={onForNotClientClick}
                    icon={<Person />}
                  />
                </>
              )}
            </>
          ) : (
            <Loader />
          )}
        </ListBody>
      </>
    );
  }
);
